import { Badge, Button, Card, Group, Modal, ScrollArea, Stack, Text, UnstyledButton } from '@mantine/core';
import { useEffect, useState } from 'react';
import { IconPlus } from '@tabler/icons-react';
import { Topic } from '../../types/TopicModels';
import { primaryColor } from '../../constants/theme';
import { TopicEditorModal } from './TopicEditorModal';

interface TopicPickerModalProps {
  opened: boolean;
  topics: Topic[];
  initialSelected: number[];
  onClose: () => void;
  onConfirm: (selectedIds: number[]) => void;
}

export const TopicPickerModal = ({
  opened,
  topics,
  initialSelected,
  onClose,
  onConfirm,
}: TopicPickerModalProps) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    () => new Set(initialSelected)
  );
  const [topicEditorOpen, setTopicEditorOpen] = useState(false);

  useEffect(() => {
    if (opened) {
      setSelectedIds(new Set(initialSelected));
    }
  }, [opened]);

  const toggleTopic = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selectedIds));
  };

  const visibleTopics = topics.filter((t) => t.isActive);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      padding="lg"
      title={
        <Stack gap={2}>
          <Text fw={600} size="h4" ta="left">
            Choose Topics
          </Text>
          <Text size="sm" c="dimmed" ta="left">
            Select one or more topics to set availability against
          </Text>
        </Stack>
      }
    >
      <Stack gap="md">
        <ScrollArea.Autosize mah={400}>
          <Stack gap="sm">
            {visibleTopics.map((topic) => {
              const selected = selectedIds.has(topic.id);
              const color = topic.colour ?? primaryColor;
              return (
                <UnstyledButton
                  key={topic.id}
                  onClick={() => toggleTopic(topic.id)}
                  style={(theme) => ({
                    border: `1px solid ${selected ? color : theme.colors.gray[3]}`,
                    borderRadius: theme.radius.md,
                    padding: '10px 14px',
                    background: selected ? color : 'transparent',
                    transition: 'background 0.15s, border-color 0.15s',
                  })}
                >
                  <Text fw={600} size="sm" c={selected ? 'white' : 'dark'} ta="left">
                    {topic.title}
                  </Text>
                  <Group gap={6} wrap="nowrap" mt={4}>
                    <Badge
                      radius="sm"
                      variant={selected ? 'white' : 'light'}
                      color={selected ? 'white' : 'gray'}
                      style={{ color: selected ? color : undefined }}
                    >
                      {topic.durationMinutes ?? 30} mins
                    </Badge>
                    <Badge
                      radius="sm"
                      variant={selected ? 'white' : 'light'}
                      color={selected ? 'white' : primaryColor}
                      style={{ color: selected ? color : undefined }}
                    >
                      {topic.rate ? `£${topic.rate}` : 'Free'}
                    </Badge>
                  </Group>
                </UnstyledButton>
              );
            })}
            {visibleTopics.length === 0 && (
              <Text c="dimmed" ta="center" py="md">
                No active topics found.
              </Text>
            )}

            {/* Add Topic(s) action tile */}
            <Card
              withBorder
              radius="md"
              p="10px 14px"
              pb={12}
              style={{
                borderStyle: 'dashed',
                borderColor: primaryColor,
                borderWidth: 1,
                cursor: 'pointer',
              }}
              onClick={() => setTopicEditorOpen(true)}
            >
              <Group justify="center" align="center" gap={6}>
                <IconPlus size={16} color={primaryColor} />
                <Text fw={600} size="sm" c={primaryColor}>
                  Add Topic
                </Text>
              </Group>
            </Card>
          </Stack>
        </ScrollArea.Autosize>

        <TopicEditorModal
          opened={topicEditorOpen}
          onClose={() => setTopicEditorOpen(false)}
          editingTopic={null}
        />

        <Button
          color={primaryColor}
          fullWidth
          disabled={selectedIds.size === 0}
          onClick={handleConfirm}
        >
          Done{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
        </Button>
      </Stack>
    </Modal>
  );
};

